import React, { useState, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./Brands.css";
import "../App.css";
import * as FaIcons from "react-icons/fa";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Tips from "../components/Tips.js";
import BrandsSlider from "../components/BrandsSlider.js";
import Fancybox from "../components/Fancybox.js";

gsap.registerPlugin(ScrollTrigger);

const { useLayoutEffect, useRef } = React;

function BulleitCraftedCocktails() {
  useEffect(() => {
    // 👇 add class to body element
    document.body.classList.add("bulleit-crafted-cocktails-page");
  }, []);

  // BRAND HERO ANIMATION
  const brand = useRef();
  const brandTl = useRef();

  useLayoutEffect(() => {
    const brandAnim = gsap.context(() => {
      brandTl.current = gsap
        .timeline()
        .add("start")
        .to(
          ".brand__brand-hero",
          {
            y: 0,
            autoAlpha: 1,
            duration: 2,
            ease: "power4.inOut",
          },
          "start"
        )
        .to(
          ".brand__play-btn",
          {
            y: 0,
            autoAlpha: 1,
            duration: 2,
            ease: "power4.inOut",
          },
          "start"
        );
    }, brand);
  }, []);

  // BRAND INTRO
  const brandIntro = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandIntro = gsap.timeline({
        scrollTrigger: {
          trigger: ".brand__intro",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandIntro.to(".brand__intro-wrap", {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        ease: "power4.inOut",
      });

      return () => brandIntro.scrollTrigger.kill();
    });
  }, []);

  // BRAND SHARE
  const brandShare = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandShare = gsap.timeline({
        scrollTrigger: {
          trigger: ".brand__share",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandShare.to(".brand__share-text", {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        ease: "power4.inOut",
      });

      return () => brandShare.scrollTrigger.kill();
    });
  }, []);

  // BRAND VIDEO
  const brandVideo = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandVideo = gsap.timeline({
        scrollTrigger: {
          trigger: ".brand__video-downloads",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandVideo.add("start");
      brandVideo.to(
        ".brand__video-download-heading",
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: "power4.inOut",
        },
        "start"
      );
      brandVideo.to(
        ".brand__video-item",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );

      return () => brandVideo.scrollTrigger.kill();
    });
  }, []);

  // BRAND IMAGES
  const brandImages = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandImages = gsap.timeline({
        scrollTrigger: {
          trigger: ".brand__image-downloads",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandImages.add("start");
      brandImages.to(
        ".brand__image-download-heading",
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: "power4.inOut",
        },
        "start"
      );
      brandImages.to(
        ".brand__image-item",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );

      return () => brandImages.scrollTrigger.kill();
    });
  }, []);

  // BRAND HASHTAGS
  const brandHashtags = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandHashtags = gsap.timeline({
        scrollTrigger: {
          trigger: ".brand__hashtag-section",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandHashtags.add("start");
      brandHashtags.to(
        ".brand__hashtag-heading",
        {
          autoAlpha: 1,
          y: 0,
          stagger: 0.2,
          duration: 1,
          ease: "power4.inOut",
        },
        "start"
      );
      brandHashtags.to(
        ".brand__hashtag",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );

      return () => brandHashtags.scrollTrigger.kill();
    });
  }, []);

  // BRAND COPY
  const brandCopy = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandCopy = gsap.timeline({
        scrollTrigger: {
          trigger: ".brand__message-section",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandCopy.add("start");
      brandCopy.to(
        ".brand__message-heading",
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: "power4.inOut",
        },
        "start"
      );
      brandCopy.to(
        ".brand__message",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );

      return () => brandCopy.scrollTrigger.kill();
    });
  }, []);

  // BRAND TIPS
  const brandTips = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandTips = gsap.timeline({
        scrollTrigger: {
          trigger: ".tips__section",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandTips.add("start");
      brandTips.to(
        ".tips__heading",
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: "power4.inOut",
        },
        "start"
      );
      brandTips.to(
        ".tips__block",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );

      return () => brandTips.scrollTrigger.kill();
    });
  }, []);

  // BRAND SLIDER
  const brandSlider = useRef();

  useLayoutEffect(() => {
    window.addEventListener("load", function () {
      let brandSlider = gsap.timeline({
        scrollTrigger: {
          trigger: ".brandsslider__related",
          start: "top 80%",
          // toggleActions: "restart none none reverse",
          end: "bottom bottom",
          markers: false,
          invalidateOnRefresh: true,
        },
      });
      brandSlider.add("start");
      brandSlider.to(
        ".brandsslider__related-heading",
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: "power4.inOut",
        },
        "start"
      );
      brandSlider.to(
        ".brandsslider__related-cell a",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );
      brandSlider.to(
        ".brandsslider__related .dil-btn",
        {
          autoAlpha: 1,
          y: 0,
          delay: 0.5,
          stagger: 0.2,
          duration: 0.5,
          ease: "power4.inOut",
        },
        "start"
      );

      return () => brandSlider.scrollTrigger.kill();
    });
  }, []);

  // Copy Functions
  const [isCopied, setIsCopied] = useState(false);

  const onCopyText = () => {
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  const [isCopied2, setIsCopied2] = useState(false);

  const onCopyText2 = () => {
    setIsCopied2(true);
    setTimeout(() => {
      setIsCopied2(false);
    }, 1000);
  };

  const [isCopied3, setIsCopied3] = useState(false);

  const onCopyText3 = () => {
    setIsCopied3(true);
    setTimeout(() => {
      setIsCopied3(false);
    }, 1000);
  };

  const [isCopied4, setIsCopied4] = useState(false);

  const onCopyText4 = () => {
    setIsCopied4(true);
    setTimeout(() => {
      setIsCopied4(false);
    }, 1000);
  };

  const [isCopied5, setIsCopied5] = useState(false);

  const onCopyText5 = () => {
    setIsCopied5(true);
    setTimeout(() => {
      setIsCopied5(false);
    }, 1000);
  };

  const [isCopied6, setIsCopied6] = useState(false);

  const onCopyText6 = () => {
    setIsCopied6(true);
    setTimeout(() => {
      setIsCopied6(false);
    }, 1000);
  };

  const [isCopied7, setIsCopied7] = useState(false);

  const onCopyText7 = () => {
    setIsCopied7(true);
    setTimeout(() => {
      setIsCopied7(false);
    }, 1000);
  };

  const [isCopied8, setIsCopied8] = useState(false);

  const onCopyText8 = () => {
    setIsCopied8(true);
    setTimeout(() => {
      setIsCopied8(false);
    }, 1000);
  };

  const [isCopied9, setIsCopied9] = useState(false);

  const onCopyText9 = () => {
    setIsCopied9(true);
    setTimeout(() => {
      setIsCopied9(false);
    }, 1000);
  };

  const [isCopied10, setIsCopied10] = useState(false);

  const onCopyText10 = () => {
    setIsCopied10(true);
    setTimeout(() => {
      setIsCopied10(false);
    }, 1000);
  };

  return (
    <>
      {/* HERO */}
      <section ref={brand} className="brand__hero">
        <img
          className="brand__mobile-hero brand__hero-img brand__brand-hero"
          src="/assets/images/Bulleit-Cocktails-Posts-4x5.jpg"
          alt="Bulleit Cocktails Hero Mobile"
          title="Bulleit Cocktails Hero Mobile"
        />
        <img
          className="brand__desktop-hero brand__hero-img brand__brand-hero"
          src="/assets/images/Bulleit-Cocktails-Posts-16x9.jpg"
          alt="Bulleit Cocktails Hero Desktop"
          title="Bulleit Cocktails Hero Desktop"
        />
        <Fancybox>
          <a
            rel="noreferrer"
            className="brand__play-btn"
            data-fancybox
            href="https://vimeo.com/651768200"
            aria-label="Play Bulleit Cocktails sizzle reel."
          >
            <div className="brand__icon orange-gradient-bg">
              <FaIcons.FaPlay className=" white" />
            </div>
          </a>
        </Fancybox>
      </section>
      {/* INTRO */}
      <section ref={brandIntro} className="full brand__intro dark-gray-bg">
        <div className="brand__intro-wrap">
          <h3 className="brand__intro-heading orange">
            <span className="tan">Introducing</span> <br />
            BULLEIT CRAFTED COCKTAILS.
          </h3>

          <p className="brand__intro-copy">
            From some of the world’s best bartenders comes a bar quality,
            ultra-premium cocktail experience straight from the bottle. Two
            perfect mixes: <strong>The Bourbon Old Fashioned</strong> and{" "}
            <strong>The Rye Manhattan</strong>. Bartender-approved and great for
            groups. Just pour and serve. 375ML and 750ML sizes available now.
          </p>

          <p>
            <a
              rel="noreferrer"
              className="dil-btn"
              href="/styleguide"
              aria-label="Go to style guide"
            >
              Style Guide
            </a>
          </p>
        </div>
      </section>

      {/* SHARE BUTTONS */}
      <section
        ref={brandShare}
        className="full brand__share orange-gradient-bg"
      >
        <div className="brand__share-wrap">
          <div className="brand__share-text">
            <h3 className="white">
              <span className="partial-op">Help build our</span>{" "}
              <span className="full-op">brands</span>{" "}
              <span className="partial-op">by</span>{" "}
              <span className="full-op">SHARING</span>{" "}
              <span className="partial-op">them on your</span>{" "}
              <span className="full-op">social media.</span>
            </h3>
          </div>
          <div className="brand__share-feature">
            <h4 className="brand__share-heading heading">
              Quick Share From Our Social Accounts
            </h4>
            {/* NEW SHARE BUTTONS */}
            <a
              rel="noreferrer"
              href="https://www.facebook.com/DiageoInnovation"
              target="_blank"
              aria-label="Share Bulleit Cocktails from Facebook."
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a
              rel="noreferrer"
              href="https://twitter.com/DiageoInnovates"
              target="_blank"
              aria-label="Share Bulleit Cocktails from Twitter."
            >
              <i className="fab fa-twitter"></i>
            </a>
            <a
              rel="noreferrer"
              href="https://www.instagram.com/diageoinnovation/"
              target="_blank"
              aria-label="Share Bulleit Cocktails from Instagram."
            >
              <i className="fab fa-instagram"></i>
            </a>

            {/* FORMER SHARE BUTTONS 
                  <a rel="noreferrer" href="https://www.facebook.com/sharer/sharer.php?u=https://www.baileys.com/en-us/" aria-label="Share Bulleit Cocktails on Facebook."><i className="fab fa-facebook"></i></a>
                  <a rel="noreferrer" href="https://twitter.com/intent/tweet?url=https://www.baileys.com/en-us/&text=" aria-label="Share Bulleit Cocktails on Twitter."><i className="fab fa-twitter"></i></a>
                  <a rel="noreferrer" href="https://www.linkedin.com/sharing/share-offsite/?url=https://www.baileys.com/en-us/" aria-label="Share Bulleit Cocktails on LinkedIn."><i className="fab fa-linkedin-in"></i></a> */}
          </div>
        </div>
      </section>

      {/* VIDEO DOWNLOADS */}

      <section
        ref={brandVideo}
        id="video-downloads"
        className="full brand__video-downloads gray-bg"
      >
        <h4 className="brand__video-download-heading heading orange">
          <span className="white">DOWNLOAD</span> these READY-TO-SHARE{" "}
          <span className="white">Videos.</span>
        </h4>
        <div className="brand__video-downloads-wrapper" video-rollover="1">
          <Fancybox>
            <div className="brand__video-item">
              <div
                id="brand__video-item-1"
                className="brand__download-wrap brand__video-rollover black-bg"
              >
                <img
                  src="/assets/images/Bulleit-Cocktails-Posts-1x1.jpg"
                  alt="Bulleit Cocktails 1x1 Thumbnail"
                  title="Bulleit Cocktails 1x1 Thumbnail"
                />

                <div
                  id="brand__video-preview-1"
                  className="brand__preview-wrap"
                >
                  <iframe
                    title="brand__video-frame-1"
                    id="brand__video-frame-1"
                    className="brand__video-frame"
                    src="https://player.vimeo.com/video/651768163?api=1&loop=1&muted=1"
                    width="305px"
                    height="305px"
                    frameBorder="0"
                  ></iframe>
                </div>

                <div className="brand__video-item-btns">
                  <a
                    rel="noreferrer"
                    href="https://player.vimeo.com/external/651768163.hd.mp4?s=a46d7c04c2f6265eb15ae96f8e60582ba488e379&profile_id=175&download=1"
                    download
                    aria-label="Download Bulleit Cocktails 1x1 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaArrowDown className="white" />
                      <span>Download</span>
                    </div>
                  </a>

                  <a
                    data-fancybox
                    href="https://vimeo.com/651768163"
                    aria-label="Preview Bulleit Cocktails 1x1 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaEye className="white" />
                      <span>Preview</span>
                    </div>
                  </a>
                </div>
              </div>
              <p>
                <strong>Square 1x1</strong>
                <br />
                <em>Facebook, LinkedIn, or Instagram Posts</em>
              </p>
            </div>
            <div className="brand__video-item">
              <div
                id="brand__video-item-2"
                className="brand__download-wrap brand__video-rollover black-bg"
              >
                <img
                  src="/assets/images/Bulleit-Cocktails-Posts-9x16.jpg"
                  alt="Bulleit Cocktails 9x16 Thumbnail"
                  title="Bulleit Cocktails 9x16 Thumbnail"
                />

                <div
                  id="brand__video-preview-2"
                  className="brand__preview-wrap"
                >
                  <iframe
                    title="brand__video-frame-2"
                    id="brand__video-frame-2"
                    className="brand__video-frame"
                    src="https://player.vimeo.com/video/651768183?api=1&loop=1&muted=1"
                    width="305px"
                    height="305px"
                    frameBorder="0"
                  ></iframe>
                </div>

                <div className="brand__video-item-btns">
                  <a
                    rel="noreferrer"
                    href="https://player.vimeo.com/external/651768183.hd.mp4?s=50ab9e0cb03e3a1473044c870205b6151b7a802e&profile_id=174&download=1"
                    download
                    aria-label="Download Bulleit Cocktails 9x16 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaArrowDown className="white" />
                      <span>Download</span>
                    </div>
                  </a>

                  <a
                    data-fancybox
                    href="https://vimeo.com/651768183"
                    aria-label="Preview Bulleit Cocktails 9x16 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaEye className="white" />
                      <span>Preview</span>
                    </div>
                  </a>
                </div>
              </div>
              <p>
                <strong>Vertical 9x16</strong>
                <br />
                <em>Facebook or Instagram Stories</em>
              </p>
            </div>
            <div className="brand__video-item">
              <div
                id="brand__video-item-3"
                className="brand__download-wrap brand__video-rollover black-bg"
              >
                <img
                  src="/assets/images/Bulleit-Cocktails-Posts-4x5.jpg"
                  alt="Bulleit Cocktails 4x5 Thumbnail"
                  title="Bulleit Cocktails 4x5 Thumbnail"
                />

                <div
                  id="brand__video-preview-3"
                  className="brand__preview-wrap"
                >
                  <iframe
                    title="brand__video-frame-3"
                    id="brand__video-frame-3"
                    className="brand__video-frame"
                    src="https://player.vimeo.com/video/644947323?api=1&loop=1&muted=1"
                    width="305px"
                    height="305px"
                    frameBorder="0"
                  ></iframe>
                </div>

                <div className="brand__video-item-btns">
                  <a
                    rel="noreferrer"
                    href="https://player.vimeo.com/external/644947323.hd.mp4?s=7436a9eb749cb61e5ec16c518a9d8cd1bff001fe&profile_id=175&download=1"
                    download
                    aria-label="Download Bulleit Cocktails 4x5 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaArrowDown className="white" />
                      <span>Download</span>
                    </div>
                  </a>

                  <a
                    data-fancybox
                    href="https://vimeo.com/644947323"
                    aria-label="Preview Bulleit Cocktails 4x5 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaEye className="white" />
                      <span>Preview</span>
                    </div>
                  </a>
                </div>
              </div>
              <p>
                <strong>Vertical 4x5</strong>
                <br />
                <em>Facebook, LinkedIn, or Instagram Posts</em>
              </p>
            </div>
            <div className="brand__video-item">
              <div
                id="brand__video-item-4"
                className="brand__download-wrap brand__video-rollover black-bg"
              >
                <img
                  src="/assets/images/Bulleit-Cocktails-Posts-16x9.jpg"
                  alt="Bulleit Cocktails 16x9 Thumbnail"
                  title="Bulleit Cocktails 16x9 Thumbnail"
                />

                <div
                  id="brand__video-preview-4"
                  className="brand__preview-wrap"
                >
                  <iframe
                    title="brand__video-frame-4"
                    id="brand__video-frame-4"
                    className="brand__video-frame"
                    src="https://player.vimeo.com/video/651768200?api=1&loop=1&muted=1"
                    width="305px"
                    height="305px"
                    frameBorder="0"
                  ></iframe>
                </div>

                <div className="brand__video-item-btns">
                  <a
                    rel="noreferrer"
                    href="https://player.vimeo.com/external/651768200.hd.mp4?s=d6c1185ed37c36b1c35eaa27ecce01ecd1b6be68&profile_id=174&download=1"
                    download
                    aria-label="Download Bulleit Cocktails 16x9 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaArrowDown className="white" />
                      <span>Download</span>
                    </div>
                  </a>

                  <a
                    data-fancybox
                    href="https://vimeo.com/651768200"
                    aria-label="Preview Bulleit Cocktails 16x9 Video"
                  >
                    <div className="brand__video-download-preview">
                      <FaIcons.FaEye className="white" />
                      <span>Preview</span>
                    </div>
                  </a>
                </div>
              </div>
              <p>
                <strong>Horizontal 16x9</strong>
                <br />
                <em>Facebook, LinkedIn, or Twitter Posts</em>
              </p>
            </div>
          </Fancybox>
        </div>
      </section>

      {/* IMAGE DOWNLOADS */}
      <section
        ref={brandImages}
        className="full brand__image-downloads black-bg"
      >
        <h4 className="brand__image-download-heading heading tan">
          <span className="white">DOWNLOAD</span> these READY-TO-SHARE{" "}
          <span className="white">Images.</span>
        </h4>

        <div className="brand__image-downloads-wrapper">
          <div className="brand__image-item">
            <a
              rel="noreferrer"
              className="brand__download-wrap gray-bg"
              href="/assets/images/Bulleit-Cocktails-Posts-1x1.jpg"
              aria-label="Download Bulleit Cocktails 1x1 image"
              download
            >
              <img
                src="/assets/images/Bulleit-Cocktails-Posts-1x1.jpg"
                alt="Bulleit Cocktails 1x1 thumbnail"
                title="Bulleit Cocktails 1x1 thumbnail"
              />
              <div className="brand__image-download-preview">
                <FaIcons.FaArrowDown className="white" />
                <span>Download</span>
              </div>
            </a>
            <p>
              <strong>Square 1x1</strong>
              <br />
              <em className="baileys-graham">
                Facebook, LinkedIn, or Instagram Posts
              </em>
            </p>
          </div>
          <div className="brand__image-item">
            <a
              rel="noreferrer"
              className="brand__download-wrap gray-bg"
              href="/assets/images/Bulleit-Cocktails-Posts-9x16.jpg"
              aria-label="Download Bulleit Cocktails 9x16 image"
              download
            >
              <img
                src="/assets/images/Bulleit-Cocktails-Posts-9x16.jpg"
                alt="Bulleit Cocktails 9x16 thumbnail"
                title="Bulleit Cocktails 9x16 thumbnail"
              />
              <div className="brand__image-download-preview">
                <FaIcons.FaArrowDown className="white" />
                <span>Download</span>
              </div>
            </a>
            <p>
              <strong>Vertical 9x16</strong>
              <br />
              <em className="baileys-graham">Facebook or Instagram Stories</em>
            </p>
          </div>
          <div className="brand__image-item">
            <a
              rel="noreferrer"
              className="brand__download-wrap gray-bg"
              href="/assets/images/Bulleit-Cocktails-Posts-4x5.jpg"
              aria-label="Download Bulleit Cocktails 4x5 image"
              download
            >
              <img
                src="/assets/images/Bulleit-Cocktails-Posts-4x5.jpg"
                alt="Bulleit Cocktails 4x5 thumbnail"
                title="Bulleit Cocktails 4x5 thumbnail"
              />
              <div className="brand__image-download-preview">
                <FaIcons.FaArrowDown className="white" />
                <span>Download</span>
              </div>
            </a>
            <p>
              <strong>Vertical 4x5</strong>
              <br />
              <em className="baileys-graham">
                Facebook, LinkedIn, or Instagram Posts
              </em>
            </p>
          </div>
          <div className="brand__image-item">
            <a
              rel="noreferrer"
              className="brand__download-wrap gray-bg"
              href="/assets/images/Bulleit-Cocktails-Posts-16x9.jpg"
              aria-label="Download Bulleit Cocktails 16x9 image"
              download
            >
              <img
                src="/assets/images/Bulleit-Cocktails-Posts-16x9.jpg"
                alt="Bulleit Cocktails 16x9 thumbnail"
                title="Bulleit Cocktails 16x9 thumbnail"
              />
              <div className="brand__image-download-preview">
                <FaIcons.FaArrowDown className="white" />
                <span>Download</span>
              </div>
            </a>
            <p>
              <strong>Horizontal 16x9</strong>
              <br />
              <em className="baileys-graham">
                Facebook, LinkedIn, or Twitter Posts
              </em>
            </p>
          </div>
        </div>
      </section>

      {/* HASHTAG SECTION */}
      <section
        ref={brandHashtags}
        className="full brand__hashtag-section orange-gradient-bg"
      >
        <div className="brand__required-hashtag">
          <h4 className="brand__hashtag-heading heading black">
            REQUIRED HASHTAGS
          </h4>
          <div className="brand__hashtag-wrapper">
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#IWorkForBulleit</strong>
              </p>
              <CopyToClipboard text="#IWorkForBulleit" onCopy={onCopyText}>
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#DiageoInnovation</strong>
              </p>
              <CopyToClipboard text="#DiageoInnovation" onCopy={onCopyText2}>
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied2 ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
          </div>
        </div>
        <div className="grab-hashtag">
          <h4 className="brand__hashtag-heading heading black">
            OPTIONAL HASHTAGS
          </h4>
          <div className="brand__hashtag-wrapper">
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#FromtheLab</strong>
              </p>
              <CopyToClipboard text="#FromtheLab" onCopy={onCopyText3}>
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied3 ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#CocktailCulture</strong>
              </p>
              <CopyToClipboard text="#CocktailCulture" onCopy={onCopyText4}>
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied4 ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#Whiskey</strong>
              </p>
              <CopyToClipboard text="#Whiskey" onCopy={onCopyText5}>
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied5 ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#BulleitCraftedCocktails</strong>
              </p>
              <CopyToClipboard
                text="#BulleitCraftedCocktails"
                onCopy={onCopyText6}
              >
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied6 ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
            <div className="brand__copy-block brand__hashtag">
              <p>
                <strong className="white">#Bulleit</strong>
              </p>
              <CopyToClipboard text="#Bulleit" onCopy={onCopyText7}>
                <div className="copy-area">
                  <button className="brand__copy-btn">COPY</button>
                  <span
                    className={`brand__copy-feedback ${
                      isCopied7 ? "brand__active" : ""
                    }`}
                  >
                    Copied!
                  </span>
                </div>
              </CopyToClipboard>
            </div>
          </div>
        </div>
      </section>

      {/* MESSAGE SECTION */}
      <section ref={brandCopy} className="full brand__message-section black-bg">
        <h4 className="brand__message-heading heading">
          <span className="tan">
            NEED MESSAGE POINTS ABOUT THIS INNOVATION?
          </span>
        </h4>
        <div className="brand__message-wrapper">
          <div className="brand__copy-block brand__message">
            <h4>News Style</h4>

            <p id="NewsStyle">
              NEW from Diageo Innovation - BULLEIT CRAFTED COCKTAILS - a
              bar-quality, ultra-premium cocktail experience straight from the
              bottle. Two perfect mixes: Bourbon Old Fashioned and Rye
              Manhattan. Bartender-approved. Great for groups. Just pour and
              serve. 375ML and 750ML sizes available now.
            </p>
            <CopyToClipboard
              text="NEW from Diageo Innovation - BULLEIT CRAFTED COCKTAILS - a bar-quality, ultra-premium cocktail experience straight from the bottle. Two perfect mixes: Bourbon Old Fashioned and Rye Manhattan.  Bartender-approved. Great for groups. Just pour and serve. 375ML and 750ML sizes available now."
              onCopy={onCopyText8}
            >
              <div className="copy-area">
                <button className="brand__copy-btn">COPY</button>
                <span
                  className={`brand__copy-feedback ${
                    isCopied8 ? "brand__active" : ""
                  }`}
                >
                  Copied!
                </span>
              </div>
            </CopyToClipboard>
          </div>
          <div className="brand__copy-block brand__message">
            <h4>Casual</h4>

            <p id="Casual">
              Diageo Innovation tapped some of the world’s best bartenders to
              perfectly mix two classic, ultra-premium Bulleit Crafted Cocktails
              – the Bourbon Old Fashioned and the Rye Manhattan. An authentic
              bar-quality cocktail experience - straight from the bottle. Just
              pour and serve. 375ML and 750ML sizes available now.
            </p>
            <CopyToClipboard
              text="Diageo Innovation tapped some of the world’s best bartenders to perfectly mix two classic, ultra-premium Bulleit Crafted Cocktails – the Bourbon Old Fashioned and the Rye Manhattan. An authentic bar-quality cocktail experience - straight from the bottle. Just pour and serve. 375ML and 750ML sizes available now."
              onCopy={onCopyText9}
            >
              <div className="copy-area">
                <button className="brand__copy-btn">COPY</button>
                <span
                  className={`brand__copy-feedback ${
                    isCopied9 ? "brand__active" : ""
                  }`}
                >
                  Copied!
                </span>
              </div>
            </CopyToClipboard>
          </div>
          <div className="brand__copy-block brand__message">
            <h4>Short & Fun</h4>

            <p id="ShortFun">
              Rather mingle than mix? Serve Bulleit Crafted Cocktails. Fresh
              from The LAB. Two legendary classics: The Bourbon Old Fashioned
              and The Rye Manhattan. Just pour and serve. Straight up or over
              ice. Garnish to taste.
            </p>
            <CopyToClipboard
              text="Rather mingle than mix? Serve Bulleit Crafted Cocktails.  Fresh from The LAB. Two legendary classics: The Bourbon Old Fashioned and The Rye Manhattan. Just pour and serve. Straight up or over ice. Garnish to taste."
              onCopy={onCopyText10}
            >
              <div className="copy-area">
                <button className="brand__copy-btn">COPY</button>
                <span
                  className={`brand__copy-feedback ${
                    isCopied10 ? "brand__active" : ""
                  }`}
                >
                  Copied!
                </span>
              </div>
            </CopyToClipboard>
          </div>
        </div>
      </section>

      {/* TIPS */}
      <Tips ref={brandTips} />

      {/* BRANDS SLIDER */}
      <BrandsSlider ref={brandSlider} />
    </>
  );
}

export default BulleitCraftedCocktails;
